import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Check, Layers, LayoutGrid, Pencil, Plus, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { cn } from "@/shared/lib/utils";
import { Button } from "@/shared/components/ui/button";
import { useForm } from "@/shared/lib/forms";
import type { FormField, SelectOptionShape } from "@/shared/lib/forms";
import {
  useDashboardCatalog,
  type DashboardCatalog,
} from "@/admin/hooks/use-dashboard-config";
import {
  CARD_TITLE_MAX,
  cardBinding,
  cardTitle,
  derivedTitleFor,
} from "./card-titles";
import { getLiveSource } from "./live-sources";
import { TileBody } from "./dashboard-tiles";
import { LiveTable } from "./live-table";
import type {
  DataType,
  RenderType,
  TileDef,
  TileSpan,
} from "./dashboard-types";

// Which display types (render_type) a given data shape (data_type) supports. The
// chosen data's shape drives the display picker, so only sensible combinations
// are offered (a number can't be a map, a list can't be a pie, etc.).
const RENDER_BY_DATATYPE: Record<string, RenderType[]> = {
  snapshot_number: ["stat"],
  time_series: ["line_chart", "bar_chart", "table"],
  split_totals: ["bar_chart", "pie_chart", "leaderboard", "table"],
  worklist: ["table"],
  geo_points: ["map", "table"],
  action_list: ["alerts", "followups"],
  // Custom views draw only as their one renderer.
  notifications: ["alerts"],
  work_queue: ["work_queue"],
  activity: ["activity"],
  content: ["stat"],
};

const RENDER_LABEL: Record<RenderType, string> = {
  stat: "Number",
  table: "Table",
  leaderboard: "Leaderboard",
  bar_chart: "Bar chart",
  pie_chart: "Pie / donut",
  line_chart: "Line / trend",
  map: "Map",
  heat_map: "Heat map",
  raw_table: "List of records",
  action_grid: "Action grid",
  followups: "List",
  alerts: "Alerts feed",
  work_queue: "Work queue",
  activity: "Activity feed",
};

const SPAN_OPTIONS: { value: TileSpan; label: string }[] = [
  { value: 4, label: "One third" },
  { value: 6, label: "Half" },
  { value: 8, label: "Two thirds" },
  { value: 12, label: "Full width" },
];

// Placeholder key a draft card carries during render; swapped for a real unique
// key when the card is saved.
const NEW_CARD_KEY = "u_new";

// A unique key for a user-created card. crypto.randomUUID is undefined in
// non-secure contexts / some test envs, so fall back. Called from the save
// handler (an event), never during render.
function newCardKey(): string {
  const raw =
    crypto?.randomUUID?.() ??
    `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
  return `u_${raw.slice(0, 8)}`;
}

// A tile rendered exactly as it would appear on the dashboard, for the builder's
// live preview. Mirrors the TileCard body dispatch (live source -> LiveTable).
function TilePreview({
  tile,
  createdAfter,
}: {
  tile: TileDef;
  createdAfter?: string | null;
}) {
  const live = getLiveSource(tile.data_set?.source);
  return (
    <section className="cv-tile relative rounded-[15px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] p-5">
      <div className="mb-3 text-[11.5px] font-medium uppercase tracking-[0.12em] text-[var(--ink-2)]">
        {tile.title || "Untitled"}
      </div>
      {live ? (
        <LiveTable
          key={`preview:${tile.key}:${tile.data_set?.source}`}
          source={tile.data_set!.source!}
          binding={tile.data_set!}
          tableId={`dashboard-preview-${tile.key}`}
        />
      ) : (
        <TileBody tile={tile} onDrill={() => {}} createdAfter={createdAfter} />
      )}
    </section>
  );
}

export interface CardEditorProps {
  open: boolean;
  onClose: () => void;
  /** Visible tile keys, in order. */
  order: string[];
  /** The merged tile lookup (config + user cards). */
  tiles: Record<string, TileDef>;
  createdAfter?: string | null;
  /** Add or update a card (keyed by tile.key). */
  onSave: (tile: TileDef) => void;
  /** Remove a card from the dashboard. */
  onDelete: (key: string) => void;
  /** Enter the drag-to-rearrange mode on the dashboard. */
  onRearrange: () => void;
  /** The card just added (or picked from the list), and the views it is in. */
  justAdded?: { key: string; title: string; inViews: string[]; existing?: boolean } | null;
  /** The user's own saved views, offered as "Add to <view>". */
  views?: { uuid: string; name: string; cardKeys: string[] }[];
  /** Pick an existing card to add to a saved view. */
  onPickCardForView?: (key: string) => void;
  /** Keys of cards the user built (vs the role's seeded cards). */
  userCardKeys?: string[];
  /** Put one of the user's cards back on the dashboard. */
  onPutBack?: (key: string) => void;
  onAddToView?: (viewUuid: string) => void;
  /** Save the dashboard (with the new card) as a new view. */
  onNewView?: () => void;
}

export function CardEditor({
  open,
  onClose,
  order,
  tiles,
  createdAfter,
  onSave,
  onDelete,
  onRearrange,
  justAdded,
  views = [],
  onAddToView,
  onNewView,
  onPickCardForView,
  userCardKeys = [],
  onPutBack,
}: CardEditorProps) {
  const { t } = useTranslation();
  const { data: catalog } = useDashboardCatalog();
  const renderLabel = (rt: RenderType) =>
    t(`dashboard.customise.displayType.${rt}`, RENDER_LABEL[rt] ?? rt);
  // Built here: inside the card list below, `t` is shadowed by each tile.
  const addToViewLabel = t("dashboard.customise.addToAView", "Add to view");
  const putBackLabel = t("dashboard.customise.putBack", "Put back");
  const untitledLabel = t("dashboard.customise.untitled", "Untitled");
  const offBoardHeading = t(
    "dashboard.customise.offBoard",
    "Cards you built that aren't on this dashboard",
  );
  // A card the user built never vanishes: one that is off this dashboard (e.g.
  // after switching to the Standard view) is listed so it can be put back or
  // added to a view.
  const offBoard = userCardKeys.filter((k) => tiles[k] && !order.includes(k));

  const [mode, setMode] = useState<"list" | "form">("list");
  const [editingKey, setEditingKey] = useState<string | null>(null);
  // What the builder form opens with (a new card, or the card being edited).
  const [seed, setSeed] = useState<BuilderSeed>(EMPTY_SEED);

  const startAdd = () => {
    setEditingKey(null);
    setSeed(EMPTY_SEED);
    setMode("form");
  };
  const startEdit = (key: string) => {
    const tile = tiles[key];
    if (!tile) return;
    const binding = cardBinding(tile);
    setEditingKey(key);
    setSeed({
      // Prefill the name the card shows today, so saving keeps it.
      title: cardTitle(tile, catalog),
      entity: binding?.entity ?? "",
      measure: binding?.measure ?? "",
      measures: tile.data_set?.measures ?? COUNT_ONLY,
      renderType: tile.render_type,
      span: tile.span,
    });
    setMode("form");
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          setMode("list");
          onClose();
        }
      }}
    >
      <DialogContent
        className={cn(
          "max-w-[860px] sm:max-w-[860px]",
          // The builder is wider, so a Full width preview has room to look full.
          mode === "form" && "w-[min(1180px,calc(100vw-2rem))] max-w-[1180px] sm:max-w-[1180px]",
        )}
      >
        <DialogHeader>
          <DialogTitle>
            {mode === "list"
              ? t("dashboard.customise.title", "Customise dashboard")
              : editingKey
                ? t("dashboard.customise.editCard", "Edit card")
                : t("dashboard.customise.addCard", "Add a card")}
          </DialogTitle>
          <DialogDescription>
            {mode === "list"
              ? t(
                  "dashboard.customise.descriptionList",
                  "Add, edit, or remove the cards on your dashboard. Changes save to your profile.",
                )
              : t(
                  "dashboard.customise.descriptionForm",
                  "Pick the data and how to display it. The preview updates as you go.",
                )}
          </DialogDescription>
        </DialogHeader>

        {mode === "list" ? (
          <div className="flex flex-col gap-2">
            {justAdded && (
              <JustAddedBanner
                existing={!!justAdded.existing}
                title={justAdded.title}
                inViews={justAdded.inViews}
                views={views}
                onAddToView={onAddToView}
                onNewView={onNewView}
              />
            )}
            <div className="max-h-[52vh] overflow-y-auto pr-1">
              {order.length === 0 && (
                <p className="py-6 text-center text-[13px] text-[var(--ink-3)]">
                  No cards yet.
                </p>
              )}
              {order.map((key) => {
                const t = tiles[key];
                if (!t) return null;
                const title = cardTitle(t, catalog) || "Untitled";
                return (
                  <div
                    key={key}
                    className="flex items-center gap-3 border-b-[0.5px] border-[var(--rule)] py-2.5 last:border-b-0"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[13.5px] font-medium text-[var(--ink)]">
                        {title}
                      </div>
                      <div className="text-[11.5px] text-[var(--ink-3)]">
                        {renderLabel(t.render_type)}
                      </div>
                    </div>
                    {views.length > 0 && onPickCardForView && (
                      <Button
                        variant="ghost"
                        onClick={() => onPickCardForView(key)}
                        aria-label={`Add ${title} to a view`}
                        title={addToViewLabel}
                        className="flex h-8 items-center gap-1.5 rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] px-2.5 text-[12.5px] font-normal text-[var(--ink-2)] hover:border-[var(--green-border)] hover:text-[var(--green)]"
                      >
                        <Layers size={14} />
                        {addToViewLabel}
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      onClick={() => startEdit(key)}
                      aria-label={`Edit ${title}`}
                      title="Edit"
                      className="grid h-8 w-8 place-items-center rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] p-0 text-[var(--ink-2)] hover:border-[var(--green-border)] hover:text-[var(--green)]"
                    >
                      <Pencil size={14} />
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => onDelete(key)}
                      aria-label={`Delete ${title}`}
                      title="Delete"
                      className="grid h-8 w-8 place-items-center rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] p-0 text-[var(--ink-2)] hover:border-[var(--status-error)] hover:text-[var(--status-error)]"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                );
              })}
            </div>
            {offBoard.length > 0 && (
              <div className="mt-1 rounded-[12px] border-[0.5px] border-dashed border-[var(--rule-strong)] px-4 py-3">
                <div className="mb-1 text-[10.5px] font-medium uppercase tracking-[0.14em] text-[var(--ink-3)]">
                  {offBoardHeading}
                </div>
                {offBoard.map((key) => {
                  const tile = tiles[key];
                  const name = cardTitle(tile, catalog) || untitledLabel;
                  return (
                    <div key={key} className="flex items-center gap-3 py-2">
                      <div className="min-w-0 flex-1 truncate text-[13.5px] text-[var(--ink)]">{name}</div>
                      {views.length > 0 && onPickCardForView && (
                        <Button
                          variant="ghost"
                          onClick={() => onPickCardForView(key)}
                          aria-label={`Add ${name} to a view`}
                          title={addToViewLabel}
                          className="flex h-8 items-center gap-1.5 rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] px-2.5 text-[12.5px] font-normal text-[var(--ink-2)] hover:border-[var(--green-border)] hover:text-[var(--green)]"
                        >
                          <Layers size={14} />
                          {addToViewLabel}
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onPutBack?.(key)}
                        className="h-8 rounded-[8px] text-[12.5px] font-normal"
                      >
                        {putBackLabel}
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="flex items-center justify-between gap-2 pt-2">
              <Button
                variant="ghost"
                onClick={() => {
                  onClose();
                  onRearrange();
                }}
                className="flex items-center gap-2 text-[13px] font-normal text-[var(--ink-2)]"
              >
                <LayoutGrid size={15} /> Rearrange &amp; resize on the dashboard
              </Button>
              <Button
                onClick={startAdd}
                className="flex items-center gap-2 bg-[var(--green)] text-white hover:bg-[var(--green-hover)] hover:text-white"
              >
                <Plus size={15} /> Add a card
              </Button>
            </div>
          </div>
        ) : (
          <CardBuilder
            key={editingKey ?? "new"}
            seed={seed}
            editingKey={editingKey}
            baseTile={editingKey ? tiles[editingKey] : undefined}
            catalog={catalog}
            createdAfter={createdAfter}
            onBack={() => setMode("list")}
            onSave={(tile) => {
              onSave(tile);
              setMode("list");
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

interface BuilderSeed {
  title: string;
  entity: string;
  measure: string;
  /** Several measures side by side, where the card's grouping takes them. */
  measures: string[];
  renderType: RenderType | "";
  span: TileSpan;
}

/** The count alone: such a card stores no measures, exactly as before. */
const COUNT_ONLY = ["count"];
const isCountOnly = (measures: string[]) =>
  measures.length === 0 || (measures.length === 1 && measures[0] === COUNT_ONLY[0]);
const asList = (v: unknown): string[] => (Array.isArray(v) ? (v as string[]) : []);

const EMPTY_SEED: BuilderSeed = {
  title: "",
  entity: "",
  measure: "",
  measures: COUNT_ONLY,
  renderType: "",
  span: 6,
};

/** Show's headings: platform counts, then a claim's fields, then a product's. */
const showHeading = (key: string) =>
  key.startsWith("attr__") ? 1 : key.startsWith("pfield__") ? 2 : 0;

const CARD_FORM_ID = "dashboard-card-form";

// Preview column spans for a card width (12-column dashboard row).
const PREVIEW_SPAN: Record<number, string> = {
  4: "col-span-12 sm:col-span-4",
  6: "col-span-12 sm:col-span-6",
  8: "col-span-12 sm:col-span-8",
  12: "col-span-12",
};
const PREVIEW_REST: Record<number, string> = {
  4: "sm:col-span-8",
  6: "sm:col-span-6",
  8: "sm:col-span-4",
};

/**
 * The card builder: name, data and presentation, with a live preview. Built on
 * the generated form; Measure cascades from Source and Display as from
 * Measure (configurationData.depends_on), so only sensible combinations are
 * offered. The preview mirrors the form values as they change.
 */
function CardBuilder({
  seed,
  editingKey,
  baseTile,
  catalog,
  createdAfter,
  onBack,
  onSave,
}: {
  seed: BuilderSeed;
  editingKey: string | null;
  /** The card being edited, as it is today (keeps its own filters). */
  baseTile?: TileDef;
  catalog: DashboardCatalog | undefined;
  createdAfter?: string | null;
  onBack: () => void;
  onSave: (tile: TileDef) => void;
}) {
  const { t } = useTranslation();
  const entities = useMemo(() => catalog?.entities ?? [], [catalog]);
  const measures = useMemo(() => catalog?.measures ?? [], [catalog]);
  // Live mirror of the form for the preview (presentation only, not the form).
  const [live, setLive] = useState<BuilderSeed>(seed);

  // A platform measure's name is copy each tenant can reword, with its noun as
  // a slot; a measure on a tenant's own field ("By Fault") is that field's name.
  const measureLabel = useCallback(
    (e: (typeof entities)[number], m: (typeof measures)[number]) => {
      const served = e.measure_labels?.[m.key] ?? m.label;
      if (showHeading(m.key) > 0) return served;
      const key = e.measure_labels?.[m.key]
        ? `dashboard.measures.${e.key}.${m.key}`
        : `dashboard.measures.${m.key}`;
      return t(key, served);
    },
    [t],
  );

  // Measures each entity can fulfil, and the displays each measure supports.
  const measureOptionsByEntity = useMemo(() => {
    const renderable = measures.filter(
      (m) => (RENDER_BY_DATATYPE[m.data_type] ?? []).length > 0,
    );
    // Headings so a long list stays scannable: the platform's counts and
    // trends, then breakdowns by the tenant's own claim and product fields.
    const headings = [
      t("dashboard.customise.measureGroupCounts", "Counts and trends"),
      t("dashboard.customise.measureGroupFields", "By a claim field"),
      t("dashboard.customise.measureGroupProductFields", "By a product field"),
    ];
    return Object.fromEntries(
      entities.map((e) => [
        e.key,
        renderable
          .filter((m) => e.measures.includes(m.key))
          .map((m) => ({
            value: m.key,
            label: measureLabel(e, m),
            group: headings[showHeading(m.key)],
          }))
          // A to Z within each heading, the headings in order.
          .sort(
            (a, b) =>
              showHeading(a.value) - showHeading(b.value) ||
              a.label.localeCompare(b.label),
          ),
      ]),
    ) as Record<string, SelectOptionShape[]>;
  }, [entities, measures, t, measureLabel]);
  // The measures a card can show side by side, per Show then per Source: a
  // share of the total needs rows to share it, and money is offered only where
  // the server says (claims, to those who may see decisions).
  const metricOptions = useMemo(() => {
    const defs = catalog?.metrics ?? [];
    const byShow: Record<
      string,
      { depends_on: string; option_groups: Record<string, SelectOptionShape[]> }
    > = {};
    for (const e of entities) {
      for (const show of e.grouped ?? []) {
        const grouped =
          measures.find((m) => m.key === show)?.data_type !== "snapshot_number";
        byShow[show] ??= { depends_on: "entity", option_groups: {} };
        byShow[show].option_groups[e.key] = defs
          .filter((d) => (e.metrics ?? []).includes(d.key) && (grouped || !d.share))
          .map((d) => ({
            value: d.key,
            label: t(`dashboard.metrics.${d.key}`, d.label),
          }));
      }
    }
    return byShow;
  }, [catalog, entities, measures, t]);
  const offeredMetrics = useCallback(
    (entity: unknown, show: unknown) =>
      (metricOptions[String(show ?? "")]?.option_groups[String(entity ?? "")] ?? []).map(
        (o) => o.value,
      ),
    [metricOptions],
  );
  // Keep the measures the new Source / Show still offers; else the count.
  const keepMetrics = useCallback(
    (entity: unknown, show: unknown, current: unknown): string[] => {
      const offered = offeredMetrics(entity, show);
      const kept = asList(current).filter((m) => offered.includes(m));
      return kept.length ? kept : COUNT_ONLY.filter((m) => offered.includes(m));
    },
    [offeredMetrics],
  );
  const renderOptionsByMeasure = useMemo(
    () =>
      Object.fromEntries(
        measures.map((m) => [
          m.key,
          ((m.renders as RenderType[] | undefined) ?? [
            ...(RENDER_BY_DATATYPE[m.data_type] ?? []),
            ...((m.extra_renders ?? []) as RenderType[]),
          ]).map((rt) => ({
            value: rt,
            label:
              t(`dashboard.customise.displayType.${rt}`, RENDER_LABEL[rt] ?? rt),
          })),
        ]),
      ) as Record<string, SelectOptionShape[]>,
    [measures, t],
  );

  const fields = useMemo<FormField[]>(
    () => [
      {
        type: "text",
        name: "title",
        label: t("dashboard.customise.cardName", "Card name"),
        placeholder: t(
          "dashboard.customise.cardNamePlaceholder",
          "e.g. Claims by product",
        ),
        value: seed.title,
        maxLength: CARD_TITLE_MAX,
        autoFocus: true,
        hint: t(
          "dashboard.customise.cardNameHint",
          "Shown at the top of the card. Leave it blank to name it from its data.",
        ),
        onChange: (v) => setLive((l) => ({ ...l, title: String(v ?? "") })),
      },
      {
        type: "select",
        name: "entity",
        label: t("dashboard.customise.source", "Source"),
        placeholder: t("dashboard.customise.sourcePlaceholder", "Choose data"),
        value: seed.entity,
        options: entities.map((e) => ({ value: e.key, label: e.label })),
        required: t("dashboard.customise.sourceRequired", "Choose the data for this card"),
        onChange: (v, ctx) => {
          const entity = String(v ?? "");
          const keep = (measureOptionsByEntity[entity] ?? []).some(
            (o) => o.value === ctx?.values.measure,
          );
          if (!keep) {
            ctx?.setFieldValue("measure", "");
            ctx?.setFieldValue("renderType", "");
          }
          const metrics = keep
            ? keepMetrics(entity, ctx?.values.measure, ctx?.values.measures)
            : [];
          ctx?.setFieldValue("measures", metrics);
          setLive((l) => ({
            ...l,
            entity,
            measures: metrics,
            ...(keep ? {} : { measure: "", renderType: "" as const }),
          }));
        },
      },
      {
        type: "select",
        name: "measure",
        // What the card shows: a count, a trend, a list, or rows by a grouping
        // ("By product"). A grouping takes the Measures below.
        label: t("dashboard.customise.measure", "Show"),
        placeholder: t("dashboard.customise.measurePlaceholder", "Choose what it shows"),
        emptyPlaceholder: t("dashboard.customise.chooseSourceFirst", "Choose a source first"),
        value: seed.measure,
        configurationData: {
          depends_on: "entity",
          option_groups: measureOptionsByEntity,
        },
        required: t("dashboard.customise.measureRequired", "Choose what to count or list"),
        onChange: (v, ctx) => {
          const measure = String(v ?? "");
          const opts = renderOptionsByMeasure[measure] ?? [];
          const current = String(ctx?.values.renderType ?? "");
          // Keep the display if the new measure supports it, else default to
          // the first one it does.
          const renderType = (opts.some((o) => o.value === current)
            ? current
            : (opts[0]?.value ?? "")) as RenderType | "";
          ctx?.setFieldValue("renderType", renderType);
          const metrics = keepMetrics(ctx?.values.entity, measure, ctx?.values.measures);
          ctx?.setFieldValue("measures", metrics);
          setLive((l) => ({ ...l, measure, renderType, measures: metrics }));
        },
      },
      {
        type: "multiSelect",
        name: "measures",
        label: t("dashboard.customise.measures", "Measures"),
        hint: t(
          "dashboard.customise.measuresHint",
          "Pick one or more. Several show as a table, or as numbers when nothing is grouped.",
        ),
        chips: true,
        value: seed.measures,
        configurationData: { depends_on: "measure", option_groups: metricOptions },
        // Shown when there is a choice (Count alone is not one).
        visible: (ctx) => offeredMetrics(ctx.values.entity, ctx.values.measure).length > 1,
        required: t("dashboard.customise.measuresRequired", "Choose at least one measure"),
        onChange: (v) => setLive((l) => ({ ...l, measures: asList(v) })),
      },
      {
        type: "select",
        name: "renderType",
        label: t("dashboard.customise.display", "Display as"),
        placeholder: t("dashboard.customise.displayPlaceholder", "Choose a display"),
        emptyPlaceholder: t(
          "dashboard.customise.chooseMeasureFirst",
          "Choose what it shows first",
        ),
        value: seed.renderType,
        configurationData: {
          depends_on: "measure",
          option_groups: renderOptionsByMeasure,
        },
        // Several measures fix the display: a table, or numbers with no grouping.
        visible: (ctx) =>
          offeredMetrics(ctx.values.entity, ctx.values.measure).length === 0 ||
          isCountOnly(asList(ctx.values.measures)),
        required: t("dashboard.customise.displayRequired", "Choose how the card looks"),
        onChange: (v) =>
          setLive((l) => ({ ...l, renderType: String(v ?? "") as RenderType })),
      },
      {
        type: "select",
        name: "span",
        label: t("dashboard.customise.width", "Width"),
        value: String(seed.span),
        options: SPAN_OPTIONS.map((o) => ({
          value: String(o.value),
          label: t(`dashboard.customise.widthOption.${o.value}`, o.label),
        })),
        onChange: (v) => setLive((l) => ({ ...l, span: Number(v) as TileSpan })),
      },
    ],
    [
      t,
      seed,
      entities,
      measureOptionsByEntity,
      renderOptionsByMeasure,
      metricOptions,
      offeredMetrics,
      keepMetrics,
    ],
  );

  const entityDef = entities.find((e) => e.key === live.entity);
  const measureDef = measures.find((m) => m.key === live.measure);
  const draft = buildDraft(live, entityDef, measureDef, editingKey, catalog, baseTile);

  const { Form } = useForm(
    { name: CARD_FORM_ID, submit: "footer", fields },
    async (values) => {
      const v = values as {
        title?: string;
        entity: string;
        measure: string;
        measures?: unknown;
        renderType: RenderType;
        span: string;
      };
      const tile = buildDraft(
        {
          title: v.title ?? "",
          entity: v.entity,
          measure: v.measure,
          measures: asList(v.measures),
          renderType: v.renderType,
          span: Number(v.span) as TileSpan,
        },
        entities.find((e) => e.key === v.entity),
        measures.find((m) => m.key === v.measure),
        editingKey,
        catalog,
        baseTile,
      );
      if (!tile) return;
      // A typed name is the card's own; a blank one is left off, so the card
      // is named from its data (in the tenant's words) wherever it renders.
      const typed = (v.title ?? "").trim();
      const persisted: TileDef = { ...tile };
      if (typed) persisted.title = typed;
      else delete persisted.title;
      onSave(
        persisted.key === NEW_CARD_KEY
          ? { ...persisted, key: newCardKey() }
          : persisted,
      );
    },
    [entities, measures, editingKey, onSave, catalog, baseTile],
  );

  return (
    <div className="grid w-full grid-cols-1 gap-6 sm:grid-cols-[300px_minmax(0,1fr)]">
      <div className="flex flex-col gap-4">
        <Form className="flex flex-col gap-4" />
        <div className="mt-2 flex items-center justify-between gap-2">
          <Button
            variant="ghost"
            onClick={onBack}
            className="flex items-center gap-1.5 text-[13px] font-normal text-[var(--ink-2)]"
          >
            <ArrowLeft size={15} /> {t("dashboard.customise.back", "Back")}
          </Button>
          <Button
            type="submit"
            form={CARD_FORM_ID}
            className="bg-[var(--green)] text-white hover:bg-[var(--green-hover)] hover:text-white"
          >
            {editingKey
              ? t("dashboard.customise.saveCard", "Save changes")
              : t("dashboard.customise.addCardButton", "Add card")}
          </Button>
        </div>
      </div>

      {/* Live preview */}
      <div className="w-full min-w-0">
        <div className="mb-2 text-[10.5px] font-medium uppercase tracking-[0.13em] text-[var(--ink-3)]">
          {t("dashboard.customise.preview", "Preview")}
        </div>
        {draft ? (
          // The card at its real width on a dashboard row (12 columns): the
          // rest of the row is shown as space, so Third / Half / Full read
          // as they will on the dashboard.
          <div className="grid grid-cols-12 gap-3" data-testid="card-preview-row">
            <div className={cn("min-w-0", PREVIEW_SPAN[live.span] ?? "col-span-12")}>
              <TilePreview tile={draft} createdAfter={createdAfter} />
            </div>
            {live.span < 12 && (
              <div
                className={cn(
                  "grid place-items-center rounded-[15px] border-[0.5px] border-dashed border-[var(--rule)] px-2 text-center text-[11.5px] text-[var(--ink-3)] max-sm:hidden",
                  PREVIEW_REST[live.span],
                )}
              >
                {t("dashboard.customise.restOfRow", "Rest of the row")}
              </div>
            )}
          </div>
        ) : (
          <div className="grid h-[200px] place-items-center rounded-[15px] border-[0.5px] border-dashed border-[var(--rule)] text-[13px] text-[var(--ink-3)]">
            {t(
              "dashboard.customise.pickPreview",
              "Pick data and a display to preview.",
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** The card the builder's values describe, or null until it is complete. */
function buildDraft(
  values: BuilderSeed,
  entityDef: { key: string; label: string; grouped?: string[] } | undefined,
  measureDef: { key: string; label: string; data_type: string } | undefined,
  editingKey: string | null,
  catalog: DashboardCatalog | undefined,
  base?: TileDef,
): TileDef | null {
  if (!entityDef || !measureDef) return null;
  const measure = measureDef.key;
  // Several measures: a table of them, or numbers when nothing is grouped.
  const several =
    !!entityDef.grouped?.includes(measure) && !isCountOnly(values.measures);
  const renderType: RenderType | "" = several
    ? measureDef.data_type === "snapshot_number"
      ? "stat"
      : "table"
    : values.renderType;
  if (!renderType) return null;
  const title =
    values.title.trim() || derivedTitleFor(entityDef.key, measureDef.key, catalog);
  // Editing a card without changing what it shows (its Source, Show and
  // Measures): keep the card's own data - its filters and settings (e.g.
  // "only claims waiting for approval") - and change only its name, width and
  // display. Otherwise renaming a card would quietly widen it to all of its
  // source.
  const baseBinding = base ? cardBinding(base) : null;
  const isRaw = renderType === "raw_table";
  if (
    base &&
    baseBinding?.entity === entityDef.key &&
    baseBinding?.measure === measure &&
    (base.data_set?.measures ?? []).join() === (several ? values.measures.join() : "") &&
    // A raw data table is a live list; the other displays read the measure's
    // figures - switching between them rebuilds the card's data.
    (base.render_type === "raw_table") === isRaw
  ) {
    return {
      ...base,
      key: editingKey ?? base.key,
      span: values.span,
      title,
      render_type: renderType,
    };
  }
  if (several) {
    return {
      key: editingKey ?? NEW_CARD_KEY,
      span: values.span,
      title,
      data_type: measureDef.data_type as DataType,
      render_type: renderType,
      data_set: { key: `${entityDef.key}:${measure}`, measures: values.measures },
    };
  }
  if (isRaw || (measureDef.data_type === "worklist" && measure !== "list")) {
    // The records the measure counts, as a live list - which records, the
    // columns and the sort come from the catalogue (tenant-overridable).
    const preset = catalog?.raw_tables?.[`${entityDef.key}:${measure}`] ?? {};
    return {
      key: editingKey ?? NEW_CARD_KEY,
      span: values.span,
      title,
      data_type: "worklist" as DataType,
      render_type: isRaw ? "raw_table" : renderType,
      data_set: {
        source: entityDef.key,
        measure,
        limit: 20,
        ...preset,
        entity: entityDef.key,
      } as TileDef["data_set"],
    };
  }
  return {
    // A stable placeholder key during render; the real unique key is minted
    // on save (an event, not render).
    key: editingKey ?? NEW_CARD_KEY,
    span: values.span,
    title,
    data_type: (measure === "list" ? "worklist" : measureDef.data_type) as DataType,
    render_type: renderType,
    data_set:
      measure === "list"
        ? { source: entityDef.key, limit: 8 }
        : { key: `${entityDef.key}:${measure}` },
  };
}

/**
 * Right after a card is added: say where it went, and offer the one obvious
 * next step - keep it in a saved view - as buttons, without leaving Customise.
 */
function JustAddedBanner({
  existing,
  title,
  inViews,
  views,
  onAddToView,
  onNewView,
}: {
  existing: boolean;
  title: string;
  inViews: string[];
  views: { uuid: string; name: string }[];
  onAddToView?: (viewUuid: string) => void;
  onNewView?: () => void;
}) {
  const { t } = useTranslation();
  const inNames = views.filter((v) => inViews.includes(v.uuid)).map((v) => v.name);
  return (
    <div
      role="status"
      data-testid="card-just-added"
      className="mb-1 rounded-[12px] border-[0.5px] border-[var(--green-border)] bg-[var(--green-bg)] px-4 py-3"
    >
      <div className="flex items-center gap-2 text-[13.5px] font-medium text-[var(--ink)]">
        <Check size={15} className="shrink-0 text-[var(--green)]" />
        {existing
          ? inNames.length
            ? t("dashboard.customise.cardInViews", {
                defaultValue: '"{{name}}" is in {{views}}',
                name: title,
                views: inNames.map((n) => `"${n}"`).join(", "),
              })
            : t("dashboard.customise.cardInNoView", {
                defaultValue: '"{{name}}" is not in any of your views yet',
                name: title,
              })
          : inNames.length
          ? t("dashboard.customise.justAddedInViews", {
              defaultValue: '"{{name}}" is on your dashboard and in {{views}}',
              name: title,
              views: inNames.map((n) => `"${n}"`).join(", "),
            })
          : t("dashboard.customise.justAdded", {
              defaultValue: '"{{name}}" is on your dashboard',
              name: title,
            })}
      </div>
      <div className="mt-2.5 flex flex-wrap items-center gap-2">
        <span className="text-[12.5px] text-[var(--ink-2)]">
          {t("dashboard.customise.keepInView", "Keep it in a view:")}
        </span>
        {views.map((v) =>
          inViews.includes(v.uuid) ? (
            <span
              key={v.uuid}
              className="inline-flex h-8 items-center gap-1.5 rounded-[8px] px-2.5 text-[12.5px] text-[var(--green)]"
            >
              <Check size={13} />
              {t("dashboard.customise.inView", { defaultValue: 'In "{{view}}"', view: v.name })}
            </span>
          ) : (
            <Button
              key={v.uuid}
              variant="outline"
              size="sm"
              onClick={() => onAddToView?.(v.uuid)}
              className="h-8 rounded-[8px] bg-[var(--bg-card)] text-[12.5px] font-normal"
            >
              {t("dashboard.customise.addToView", { defaultValue: 'Add to "{{view}}"', view: v.name })}
            </Button>
          ),
        )}
        <Button
          size="sm"
          onClick={() => onNewView?.()}
          className="h-8 gap-1 rounded-[8px] bg-[var(--green)] text-[12.5px] font-medium text-white hover:bg-[var(--green-hover)] hover:text-white"
        >
          <Plus size={13} />
          {t("dashboard.customise.newView", "Save as a new view")}
        </Button>
      </div>
    </div>
  );
}
